import { ComponentPropsWithoutRef, useState } from "react";
import { Button } from "react-bootstrap";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import ConfirmCancelModal from "../confirm-cancel-modal/ConfirmCancelModal";
import confirmSessionAttendance from "services/online-offline/confirmSessionAttendance";

import styles from "./ConfirmAttendanceModal.module.scss";
import { AmplitudePageName } from "utils/amplitude";
import { useDispatch } from "react-redux";
import { closeSidesheet } from "store/calendarSidebarSlice";

type Props = {
  appointment: AppointmentMetaData;
  amplitudePageName: AmplitudePageName;
} & Pick<ComponentPropsWithoutRef<typeof ModalLayout>, "show" | "onHide">;

const ConfirmAttendanceModal = ({
  appointment,
  show,
  onHide,
  amplitudePageName,
}: Props) => {
  const [showCancelSessionModal, setShowCancelSessionModal] = useState(false);
  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const dispatch = useDispatch();

  const handleHide = () => {
    setError(undefined);
    setBusy(false);
    onHide();
  };

  const confirmSession = async () => {
    setBusy(true);
    try {
      await confirmSessionAttendance(
        appointment.appointment_id,
        "attended_outside",
        null,
        null,
      );
      handleHide();
      dispatch(closeSidesheet());
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {appointment && (
        <ConfirmCancelModal
          show={showCancelSessionModal}
          onHide={() => setShowCancelSessionModal(false)}
          appointmentIds={[appointment.appointment_id.toString()]}
          appointmentPrice={appointment.pricing.list_price.toFixed(2)}
          cancelAll={false}
          amplitudePageName={amplitudePageName}
        />
      )}

      <ModalLayout
        disabled={busy}
        error={error}
        showHeaderDivider={false}
        onHide={onHide}
        show={show}
        size={"md"}
        backdrop={true}
        Header={"Confirm your appointment"}
        Footer={
          <>
            <Button
              variant={"outline-primary"}
              className={styles["confirm-attendance__cancel"]}
              onClick={() => {
                setShowCancelSessionModal(true);
                // hide confirm attendance modal
                onHide();
              }}
            >
              Cancelled session
            </Button>
            <Button
              className={styles["confirm-attendance__apply"]}
              onClick={confirmSession}
            >
              Confirm charge
            </Button>
          </>
        }
        headerClass={styles["confirm-attendance__modal-header"]}
        bodyClass={styles["confirm-attendance__modal-body"]}
        footerClass={styles["confirm-attendance__modal-footer"]}
        className={styles["confirm-attendance__modal"]}
      >
        <div>
          <p>
            Our system indicates that there are some pending charges that need
            your confirmation.
          </p>
          <ul>
            <li className={"mb-3"}>
              Click <b>Confirm charge</b> if you had a session with your
              patient. Your payment will be processed upon confirmation.
            </li>
            <li>
              Click <b>Cancelled session</b> if you didn't have a session with
              your patient.
            </li>
          </ul>
        </div>
      </ModalLayout>
    </>
  );
};

export default ConfirmAttendanceModal;
